import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth';
import { usePregnancyStore } from '@/store/pregnancyStore';
import type {
  ContractionSettings,
  HospitalRule,
  NotificationMode,
  StatsUpdateInterval,
} from '../types';

interface UseContractionSettingsReturn {
  settings: ContractionSettings | null;
  isLoading: boolean;
  error: string | null;
  hasSettings: boolean;
  saveSettings: (input: {
    hospital_rule: HospitalRule;
    notification_mode: NotificationMode;
    stats_update_interval: StatsUpdateInterval;
  }) => Promise<boolean>;
  updateSettings: (input: Partial<{
    hospital_rule: HospitalRule;
    notification_mode: NotificationMode;
    stats_update_interval: StatsUpdateInterval;
  }>) => Promise<boolean>;
  refetch: () => Promise<void>;
}

export function useContractionSettings(): UseContractionSettingsReturn {
  const { user } = useAuth();
  const { currentPregnancy } = usePregnancyStore();
  const [settings, setSettings] = useState<ContractionSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const userId = user?.id;
  const pregnancyId = currentPregnancy?.id;

  // Fetch settings
  const fetchSettings = useCallback(async () => {
    if (!userId || !pregnancyId) {
      setSettings(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('contraction_settings')
        .select('*')
        .eq('user_id', userId)
        .eq('pregnancy_id', pregnancyId)
        .maybeSingle();

      if (fetchError) throw fetchError;
      setSettings(data);
    } catch (err) {
      console.error('Error fetching contraction settings:', err);
      setError('Fehler beim Laden der Einstellungen');
    } finally {
      setIsLoading(false);
    }
  }, [userId, pregnancyId]);

  // Initial fetch
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Save initial settings (upsert)
  const saveSettings = useCallback(
    async (input: {
      hospital_rule: HospitalRule;
      notification_mode: NotificationMode;
      stats_update_interval: StatsUpdateInterval;
    }): Promise<boolean> => {
      if (!userId || !pregnancyId) return false;

      try {
        setError(null);

        const { data, error: upsertError } = await supabase
          .from('contraction_settings')
          .upsert(
            {
              user_id: userId,
              pregnancy_id: pregnancyId,
              hospital_rule: input.hospital_rule,
              notification_mode: input.notification_mode,
              stats_update_interval: input.stats_update_interval,
            },
            {
              onConflict: 'user_id,pregnancy_id',
            }
          )
          .select()
          .single();

        if (upsertError) throw upsertError;
        setSettings(data);
        return true;
      } catch (err) {
        console.error('Error saving contraction settings:', err);
        setError('Fehler beim Speichern der Einstellungen');
        return false;
      }
    },
    [userId, pregnancyId]
  );

  // Update existing settings
  const updateSettings = useCallback(
    async (
      input: Partial<{
        hospital_rule: HospitalRule;
        notification_mode: NotificationMode;
        stats_update_interval: StatsUpdateInterval;
      }>
    ): Promise<boolean> => {
      if (!userId || !pregnancyId || !settings) return false;

      try {
        setError(null);

        const updateData: Record<string, unknown> = {
          updated_at: new Date().toISOString(),
        };

        if (input.hospital_rule !== undefined) {
          updateData.hospital_rule = input.hospital_rule;
        }
        if (input.notification_mode !== undefined) {
          updateData.notification_mode = input.notification_mode;
        }
        if (input.stats_update_interval !== undefined) {
          updateData.stats_update_interval = input.stats_update_interval;
        }

        const { data, error: updateError } = await supabase
          .from('contraction_settings')
          .update(updateData)
          .eq('user_id', userId)
          .eq('pregnancy_id', pregnancyId)
          .select()
          .single();

        if (updateError) throw updateError;
        setSettings(data);
        return true;
      } catch (err) {
        console.error('Error updating contraction settings:', err);
        setError('Fehler beim Aktualisieren der Einstellungen');
        return false;
      }
    },
    [userId, pregnancyId, settings]
  );

  return {
    settings,
    isLoading,
    error,
    hasSettings: settings !== null,
    saveSettings,
    updateSettings,
    refetch: fetchSettings,
  };
}
